/**
 * NMS Color Swatches Component
 * Extends NMSBaseClass following NMS Component Instance Architecture
 * Provides progressive enhancement for color-as-product switching
 * Uses Shopify Section Rendering API for seamless product updates
 */

class NMSColorSwatches extends window.NMSBaseClass {
  #isInitialized = false;
  #eventListeners = [];

  constructor(element) {
    super();
    this.element = element; // REQUIRED: Store element reference
    this.swatchId = element.dataset.swatchId || `swatches-${Date.now()}`; // REQUIRED: Unique identifier

    // Store component data from Liquid
    this.sectionId = element.dataset.sectionId;
    this.productId = element.dataset.productId;
    this.mainSectionId = element.dataset.mainSectionId;
    this.sectionWrapperId = element.dataset.sectionWrapperId;

    // REQUIRED: Store in global registry for external access
    window.neemans.colorSwatches = window.neemans.colorSwatches || {};
    window.neemans.colorSwatches[this.swatchId] = this;
  }

  init() {
    if (this.#isInitialized) return;

    super.init();
    this.#setupColorSwatchesRefs();
    this.#initializeColorState();
    this.#bindColorEvents();
    this.#setupPopStateHandler();
    this.#isInitialized = true;
  }

  // REQUIRED: Scope all setup to this.element
  #setupColorSwatchesRefs() {
    // Query only within component scope using data attributes
    this.refs.swatchList = this.element.querySelector('[ref="swatchList"]');
    this.refs.swatches = this.element.querySelectorAll('[ref="swatches[]"]');
  }

  #initializeColorState() {
    // Set up global data in window.neemans namespace
    this.setGlobalData(`colorSwatches.${this.swatchId}`, {
      currentProduct: this.productId,
      isLoading: false,
      activeHandle: null,
    });
  }

  #bindColorEvents() {
    // Use inherited event binding system from NMSBaseClass
    // This will automatically handle all [data-nms-click] elements within this.element
    const clickElements = this.element.querySelectorAll('[data-nms-click]');

    for (const element of clickElements) {
      const handlerPath = element.getAttribute('data-nms-click');
      const [methodName, ...args] = handlerPath.split('/').filter(Boolean);

      if (this[methodName] && typeof this[methodName] === 'function') {
        const handler = event => {
          this[methodName].call(this, event, ...args);
        };

        element.addEventListener('click', handler, { capture: true });
        this.#eventListeners.push({ element, event: 'click', handler });
      } else {
        console.error('Method not found:', methodName);
      }
    }
  }

  // Public API method - called via data-nms-click
  async handleSwatchClick(event, productHandle) {
    event.preventDefault();
    event.stopPropagation();

    const swatchLink = event.currentTarget;

    // Don't do anything if clicking the same product
    if (swatchLink.classList.contains('nms-color-swatches__swatch--active')) {
      return;
    }

    try {
      // Get URL and title from data attributes
      const productUrl = swatchLink.dataset.productUrl;
      const productTitle = swatchLink.dataset.productTitle;

      // Update global state
      this.setGlobalData(`colorSwatches.${this.swatchId}.isLoading`, true);

      await this.#switchToProduct(productUrl, productHandle, productTitle);

      // Dispatch success event
      this.dispatchNMSEvent('color-swatch:changed', {
        swatchId: this.swatchId,
        productHandle: productHandle,
        productUrl: productUrl,
        productTitle: productTitle,
        component: this,
      });
    } catch (error) {
      const errorMessage = this.handleError(error, 'Failed to switch color. Redirecting...');
      console.error('Color switch failed:', error);

      // Fallback to hard reload
      setTimeout(() => {
        window.location.href = swatchLink.dataset.href || swatchLink.dataset.productUrl;
      }, 1000);
    } finally {
      this.setGlobalData(`colorSwatches.${this.swatchId}.isLoading`, false);
    }
  }

  async #switchToProduct(productUrl, productHandle, productTitle) {
    // Show loading state using inherited method
    this.#showLoading();

    try {
      // Fetch the new product content using Section Rendering API
      const response = await fetch(`${productUrl}?sections=${this.mainSectionId}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data[this.mainSectionId]) {
        throw new Error(`Invalid response: missing ${this.mainSectionId} section`);
      }

      // Update the main product section
      await this.#updateProductSection(data[this.mainSectionId]);

      // Update the browser URL
      this.#updateUrl(productUrl, productTitle);

      // Update active state
      this.#updateActiveState(productHandle);

      // Update global state
      this.setGlobalData(`colorSwatches.${this.swatchId}.currentProduct`, productHandle);
      this.setGlobalData(`colorSwatches.${this.swatchId}.activeHandle`, productHandle);
    } finally {
      this.#hideLoading();
    }
  }

  async #updateProductSection(newHtml) {
    // Target the entire section wrapper
    const sectionWrapper = document.querySelector(`#${this.sectionWrapperId}`);

    if (!sectionWrapper) {
      throw new Error('Main section wrapper not found');
    }

    // Create temporary container to parse new HTML
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = newHtml;
    const newSectionWrapper = tempContainer.querySelector(`#${this.sectionWrapperId}`);

    if (!newSectionWrapper) {
      throw new Error('New section wrapper not found in response');
    }

    // Replace the entire section content
    sectionWrapper.innerHTML = newSectionWrapper.innerHTML;

    // Re-initialize components in the new content
    this.#reinitializeComponents(sectionWrapper);
  }

  #reinitializeComponents(container) {
    // Find new color swatches element in updated content
    const newColorSwatches = container.querySelector('[data-nms-color-swatches]');

    if (newColorSwatches && newColorSwatches !== this.element) {
      // Update element reference and re-bind events
      this.element = newColorSwatches;
      this.#bindColorEvents();
    }

    // Fire NMS custom event for other components to re-initialize
    this.dispatchNMSEvent(
      'color-content-updated',
      {
        container: container,
        source: 'color-swatches',
        swatchId: this.swatchId,
        component: this,
      },
      container
    );
  }

  #updateUrl(newUrl, productTitle) {
    const newState = {
      product: productTitle,
      url: newUrl,
      timestamp: Date.now(),
      swatchId: this.swatchId,
    };

    history.replaceState(newState, productTitle, newUrl);

    // Update page title
    document.title = document.title.replace(/^[^|]*/, productTitle);

    // Dispatch URL update event
    this.dispatchNMSEvent('color-swatch:url-updated', {
      swatchId: this.swatchId,
      newUrl: newUrl,
      productTitle: productTitle,
      component: this,
    });
  }

  #updateActiveState(activeHandle) {
    // Remove active class from all swatches
    const allSwatches = this.element.querySelectorAll('.nms-color-swatches__swatch');

    for (const swatch of allSwatches) {
      swatch.classList.remove('nms-color-swatches__swatch--active');
    }

    // Add active class to current swatch
    const activeSwatch = this.element.querySelector(`[data-product-handle="${activeHandle}"]`);

    if (activeSwatch) {
      activeSwatch.classList.add('nms-color-swatches__swatch--active');
    }

    // Dispatch active state change event
    this.dispatchNMSEvent('color-swatch:active-changed', {
      swatchId: this.swatchId,
      activeHandle: activeHandle,
      component: this,
    });
  }

  #showLoading() {
    if (this.element) {
      this.element.style.opacity = '0.6';
      this.element.style.pointerEvents = 'none';
      this.element.setAttribute('aria-busy', 'true');
      this.element.classList.add('nms-loading');
    }
  }

  #hideLoading() {
    if (this.element) {
      this.element.style.opacity = '';
      this.element.style.pointerEvents = '';
      this.element.setAttribute('aria-busy', 'false');
      this.element.classList.remove('nms-loading');
    }
  }

  #setupPopStateHandler() {
    // Disabled: No history management needed - replaceState prevents multiple entries
    // Users will navigate back to their original referrer (homepage, collection, etc.)
  }

  // Public API method for external access
  getCurrentProductHandle() {
    return this.getGlobalData(`colorSwatches.${this.swatchId}.activeHandle`);
  }

  // Public API method for external access
  isLoading() {
    return this.getGlobalData(`colorSwatches.${this.swatchId}.isLoading`) || false;
  }

  destroy() {
    // Clean up event listeners
    for (const listener of this.#eventListeners) {
      listener.element.removeEventListener(listener.event, listener.handler);
    }
    this.#eventListeners = [];

    // Clean up global reference
    if (window.neemans.colorSwatches?.[this.swatchId]) {
      delete window.neemans.colorSwatches[this.swatchId];
    }

    super.destroy();
    this.#isInitialized = false;
  }
}

// REQUIRED: Standard initialization pattern
function initNMSColorSwatches() {
  if (!window.NMSBaseClass) {
    console.error('NMSColorSwatches: NMSBaseClass required');
    return;
  }

  // Find all color swatch components
  const elements = document.querySelectorAll('[data-nms-color-swatches]');

  for (const element of elements) {
    if (!element.hasAttribute('data-initialized')) {
      const instance = new NMSColorSwatches(element);
      instance.init();
      element.setAttribute('data-initialized', 'true');
    }
  }
}

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSColorSwatches);
} else {
  initNMSColorSwatches();
}

// Handle theme editor reloads
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('[data-nms-color-swatches]')) {
    initNMSColorSwatches();
  }
});

// Make available in global namespace
window.neemans = window.neemans || {};
window.neemans.NMSColorSwatches = NMSColorSwatches;

// Global initialization function for other components to use
window.initNMSColorSwatches = initNMSColorSwatches;
